import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchAdminMainMenu } from 'api/mainMenu.service';
import { MainMenuItemEditModel } from 'types/entity';
import { ulid } from 'ulid';

export const MAIN_MENU_UPDATE_STORE_NAME = 'mainMenuUpdate';

export const loadAdminMainMenu = createAsyncThunk(
  `${MAIN_MENU_UPDATE_STORE_NAME}/load`,
  async (arg: {projectId: string, mainMenuId: string}, thunkAPI) => {
    const response = await fetchAdminMainMenu(arg.projectId, arg.mainMenuId);
    return response;
  },
);

export interface MainMenuUpdateState {
  /** 適用済みデータ */
  fixedEntities: { [main_menu_id: string]: MainMenuItemEditModel[] }
  /** 編集中データ(ガジェット指定) */
  editingItems: MainMenuItemEditModel[]
  /** 編集中メインメニューID（ガジェットコンテンツID） */
  editingMainMenuId: string
  /** ロード済み */
  loaded: boolean
  /** 編集中か */
  touched: boolean
  /** 一度でも適用したことがあるか */
  applied: boolean
  error: any
}

export const initialMainMenuUpdateState: MainMenuUpdateState = {
  fixedEntities: {},
  editingItems: [],
  editingMainMenuId: '',
  loaded: false,
  touched: false,
  applied: false,
  error: null,
};

export const mainMenuUpdateSlice = createSlice({
  name: MAIN_MENU_UPDATE_STORE_NAME,
  initialState: initialMainMenuUpdateState,
  reducers: {
    startEdit: (state, action: PayloadAction<string>) => {
      const mainMenuId: string = action.payload;
      const editing: MainMenuItemEditModel[] = state.fixedEntities[mainMenuId] || [];
      state.editingMainMenuId = mainMenuId;
      state.editingItems = editing;
    },
    cancelEdit: (state, _action: PayloadAction<undefined>) => {
      state.editingMainMenuId = '';
      state.editingItems = [];
      state.touched = false;
    },
    clearAll: (state, _action: PayloadAction<undefined>) => {
      state.fixedEntities = {};
      state.editingItems = [];
      state.editingMainMenuId = '';
      state.loaded = false;
      state.touched = false;
      state.applied = false;
      state.error = null;
    },
    apply: (state, _action: PayloadAction<undefined>) => {
      state.fixedEntities = {
        ...state.fixedEntities,
        [state.editingMainMenuId]: state.editingItems,
      };
      state.editingMainMenuId = '';
      state.editingItems = [];
      state.touched = true;
      state.applied = true;
    },
    unApplied: (state, _action: PayloadAction<undefined>) => {
      state.touched = false;
      state.applied = false;
    },
    upsertItem: (state, action: PayloadAction<MainMenuItemEditModel>) => {
      const newest: MainMenuItemEditModel = {
        ...action.payload,
        main_menu_item_id: action.payload.main_menu_item_id || ulid(),
        display_order: action.payload.display_order || state.editingItems.length + 1, // 1始まりで採番
      };
      state.editingItems = [
        ...state.editingItems.filter((item) => item.main_menu_item_id !== newest.main_menu_item_id),
        newest,
      ];
      state.touched = true;
    },
    // 移動、表示名編集、削除を含む更新
    replaceItems: (state, action: PayloadAction<MainMenuItemEditModel[]>) => {
      state.editingItems = action.payload;
      state.touched = true;
    },
    // メインメニュー追加時に空の項目を作成する
    addEmptyMainMenu: (state, action: PayloadAction<{ mainMenuId: Required<MainMenuItemEditModel>['main_menu_id']}>) => {
      state.fixedEntities = {
        ...state.fixedEntities,
        [action.payload.mainMenuId]: [],
      };
    },
  },
  extraReducers: (builder) => {
    // ロード
    builder.addCase(loadAdminMainMenu.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(loadAdminMainMenu.fulfilled, (state, action) => {
      const { main_menu_id, main_menu_items } = action.payload;
      const items = main_menu_items.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      state.fixedEntities = {
        ...state.fixedEntities,
        [main_menu_id || '']: items,
      };
      state.loaded = true;
    });
    builder.addCase(loadAdminMainMenu.rejected, (state, action) => {
      state.error = action.payload;
      state.loaded = true;
    });
  },
});

export const mainMenuUpdateActions = {
  ...mainMenuUpdateSlice.actions,
  loadAdminMainMenu,
};

export default mainMenuUpdateSlice.reducer;
